import React from 'react';
import {
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export type SwitchTone =
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export interface SwitchProps extends Omit<MuiSwitchProps, 'color'> {
  tone?: SwitchTone;
}

interface StyledSwitchProps extends SwitchProps {
  tone: SwitchTone;
}

const StyledSwitch = styled(MuiSwitch, {
  shouldForwardProp: (property) => property !== 'tone',
})<StyledSwitchProps>(({ theme, tone }) => {
  const activeColor = theme.palette[tone].main;

  return {
    display: 'inline-flex',
    flexShrink: 0,
    width: 44,
    height: 24,
    padding: 0,
    overflow: 'visible',
    verticalAlign: 'middle',
    '& .MuiSwitch-switchBase': {
      boxSizing: 'border-box',
      top: 0,
      left: 0,
      width: 24,
      height: 24,
      margin: 0,
      padding: 2,
      transitionDuration: '160ms',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        outline: `2px solid ${alpha(activeColor, 0.38)}`,
        outlineOffset: 2,
      },
      '&.Mui-checked': {
        color: theme.palette.common.white,
        transform: 'translateX(20px)',
        '& + .MuiSwitch-track': {
          backgroundColor: activeColor,
          borderColor: activeColor,
          opacity: 1,
        },
      },
      '&.Mui-disabled': {
        color: theme.palette.text.secondary,
        opacity: 0.62,
        '& + .MuiSwitch-track': {
          opacity: 0.45,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    },
    '& .MuiSwitch-track': {
      boxSizing: 'border-box',
      width: 44,
      height: 24,
      border: '1px solid',
      borderColor: theme.palette.outline,
      borderRadius: 999,
      backgroundColor: theme.palette.sunken,
      opacity: 1,
      transition: theme.transitions.create(
        ['background-color', 'border-color'],
        { duration: 160 }
      ),
    },
  };
});

const Switch = React.memo(
  ({ tone = 'primary', ...props }: SwitchProps) => (
    <StyledSwitch {...props} tone={tone} />
  )
);

Switch.displayName = 'Switch';

export { Switch };
export * from './SwitchLabel';
